import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AppShell, Skeleton } from '@etnas/design-system';
import { readResume, saveResume, useAuth } from '../auth';
import { usePatientSlots } from '../patientSlots';
import { workspaceLanding, patientHome } from '../workspaces';
import { AppRail } from './AppRail';
import { AppTopBar } from './AppTopBar';
import { SlotTabs } from './SlotTabs';
import { SystemBanners } from './SystemBanners';
import { CommandSearch } from './CommandSearch';
import { SessionTimeout } from '../sessionTimeout';
import { LockScreen } from '@/features/auth/LockScreen';
import type { RoleKey } from '@/api';
import { StateBlock } from '@/components/States';

/** Lets nested routes fill the reference drawer (zone 8) and sign tray (zone 9). */
interface ZonesCtx {
  setDrawer: (n: ReactNode | null) => void;
  setTray: (n: ReactNode | null) => void;
}
const Zones = createContext<ZonesCtx | null>(null);
export function useShellZones() {
  const z = useContext(Zones);
  if (!z) throw new Error('useShellZones outside ShellFrame');
  return z;
}

/** Fill the drawer zone while the calling component is mounted. */
export function useDrawer(node: ReactNode | null, deps: unknown[]) {
  const { setDrawer } = useShellZones();
  useEffect(() => {
    setDrawer(node);
    return () => setDrawer(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * The workspace frame: rail, system bar, top bar, patient slots, then route-provided banner/index/canvas/drawer/tray.
 * Same nine zones on every screen (guideline 10).
 */
export function ShellFrame({ banner, index, canvas, flush }: { banner?: ReactNode; index?: ReactNode; canvas: ReactNode; flush?: boolean }) {
  const { me, lock } = useAuth();
  const { slots } = usePatientSlots();
  const location = useLocation();
  const nav = useNavigate();
  const [search, setSearch] = useState(false);
  const [drawer, setDrawer] = useState<ReactNode | null>(null);
  const [tray, setTray] = useState<ReactNode | null>(null);
  const role = me?.context.role.key;
  const frameRef = useRef<HTMLDivElement>(null);
  // `inert` keeps keyboard and AT out of the locked workspace (React 18 has no inert prop).
  useEffect(() => {
    frameRef.current?.toggleAttribute('inert', !!lock);
  }, [lock]);
  const canSearch = !!me?.permissions.some(p => p === 'patient.search' || p === 'patient.lookup');

  // Remember place for resume after reload/timeout (same person only).
  useEffect(() => {
    if (me) saveResume({ userId: me.user.id, path: location.pathname + location.search, at: Date.now() });
  }, [me, location.pathname, location.search]);

  // ⌘K / Ctrl+K search; Alt+1..4 patient slots.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && canSearch) {
        e.preventDefault();
        setSearch(true);
      } else if (e.altKey && ['1', '2', '3', '4'].includes(e.key)) {
        const s = slots.find((x) => x.slot === Number(e.key));
        if (s && role) {
          e.preventDefault();
          const to = patientHome(role, s.patientId, me?.context.roleId);
          if (to) nav(to);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canSearch, slots, role, me?.context.roleId, nav]);

  return (
    <Zones.Provider value={{ setDrawer, setTray }}>
      <a className="ex-skip" href="#main">
        Skip to main content
      </a>
      <div className="ex-appframe" ref={frameRef} aria-hidden={lock ? true : undefined} style={lock ? { filter: 'blur(3px)' } : undefined}>
        <AppShell
          rail={<AppRail />}
          sysbar={<SystemBanners />}
          topbar={<AppTopBar onSearch={canSearch ? () => setSearch(true) : undefined} />}
          tabs={<SlotTabs />}
          banner={banner}
          index={index}
          canvas={
            <div id="main" tabIndex={-1} className={'ex-stack' + (flush ? '' : '')} style={{ minHeight: '100%', outline: 'none' }}>
              {canvas}
            </div>
          }
          drawer={drawer ?? undefined}
          tray={tray ?? undefined}
        />
      </div>
      {search ? <CommandSearch onClose={() => setSearch(false)} /> : null}
      <SessionTimeout />
      <LockScreen />
    </Zones.Provider>
  );
}

/** Cross-patient workspaces (Today, Inbox, queues, admin…): no banner (guideline 10). */
export function WorkspaceLayout() {
  return <ShellFrame canvas={<Outlet />} />;
}

/** Route protection: signed-in workforce only; otherwise to the right sign-in. */
export function RequireWorkforce({ children }: { children: ReactNode }) {
  const { status, me } = useAuth();
  const loc = useLocation();
  if (status === 'restoring') return <FullPageLoading />;
  if (status === 'signed-out' || !me) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  if (me.user.kind === 'patient') return <Navigate to="/portal" replace />;
  return <>{children}</>;
}

/** Role gate for a route. Shows which role grants it (never a blank page). */
export function Guard({ roles, children, what, permission }: { roles: RoleKey[]; children: ReactNode; what?: string; permission?: string }) {
  const { me } = useAuth();
  if (!me) return null;
  if (roles.includes(me.context.role.key) && (!permission || me.permissions.includes(permission))) return <>{children}</>;
  if (permission && !me.permissions.includes(permission)) return <StateBlock kind="unauthorized" title={`${what ?? 'This area'} is outside your current permissions`} body="Switch to an authorized working context. Your current role does not grant this action." />;
  const holds = me.roles.filter((r) => roles.includes(r.key));
  return (
    <StateBlock
      kind="unauthorized"
      title={`${what ?? 'This area'} is outside your current role`}
      body={
        holds.length
          ? `You also hold ${holds.map((h) => h.name).join(', ')}. Switch role from the working-context menu in the top bar to use it.`
          : `It needs the ${roles.map((r) => r.replace('_', ' ')).join(' or ')} role. Your administrator can grant it.`
      }
    />
  );
}

export function RootRedirect() {
  const { me, status } = useAuth();
  const loc = useLocation();
  if (status === 'restoring') return <FullPageLoading />;
  if (!me) return <Navigate to="/login" replace />;
  if (me.user.kind === 'patient') return <Navigate to="/portal" replace />;
  const r = (loc.state as { resume?: ReturnType<typeof readResume> } | null)?.resume ?? readResume();
  if (r && r.userId === me.user.id && Date.now() - r.at < 12 * 3600_000 && r.path !== '/' && !r.path.startsWith('/login')) return <Navigate to={r.path} replace />;
  return <Navigate to={workspaceLanding(me.context.roleId, me.context.role.key)} replace />;
}

export function FullPageLoading() {
  return (
    <div className="ex-auth" role="status" aria-busy="true" aria-label="Loading ETNAS">
      <div className="ex-auth-card">
        <div className="ex-auth-brand">
          <span className="ex-rail-mark" aria-hidden="true">E</span>
          <span className="ex-strong">Restoring your session…</span>
        </div>
        <Skeleton rows={3} />
      </div>
    </div>
  );
}
